import { buildUniqueSelector } from './selector-utils';

export class AccessibleNamePreferredSourceRule {
	constructor(i18n) {
		this.__ = i18n;
		this.key = 'accessibleNamePreferredSource';
	}

	getDefaultStatus() {
		return 'not_checked';
	}

	evaluate(documentNode, pageUrl = '') {
		const candidateNodes = this.getCandidateNodes(documentNode);
		const elements = candidateNodes
			.map((node, index) => this.buildElementInfo(node, index, pageUrl))
			.filter((element) => element.hasVisibleNameSource);
		const issues = elements.filter((element) => element.issue.length > 0);

		if (elements.length === 0) {
			return {
				status: 'pass',
				message: this.__( 'No aria-label overrides with visible names found.', 'a11yfix' ),
				details: {
					elements,
					issues
				}
			};
		}

		if (issues.length > 0) {
			return {
				status: 'fail',
				message: `${this.__( 'Accessible name source preference issues found:', 'a11yfix' )} ${issues.length}`,
				details: {
					elements,
					issues
				}
			};
		}

		return {
			status: 'pass',
			message: this.__( 'aria-label values match visible labels/text where both are present.', 'a11yfix' ),
			details: {
				elements,
				issues
			}
		};
	}

	getCandidateNodes(documentNode) {
		return Array.from(documentNode.querySelectorAll('[aria-label]')).filter((node) => {
			if (node.closest('#wpadminbar')) {
				return false;
			}

			const ariaLabel = String(node.getAttribute('aria-label') || '').trim();
			return ariaLabel.length > 0;
		});
	}

	buildElementInfo(node, index, pageUrl) {
		const tag = node.tagName.toLowerCase();
		const type = tag === 'input' ? (node.getAttribute('type') || 'text').toLowerCase() : tag;
		const id = node.getAttribute('id') || '';
		const name = node.getAttribute('name') || '';
		const role = node.getAttribute('role') || '';
		const ariaLabel = String(node.getAttribute('aria-label') || '').trim();
		const visibleInfo = this.resolveVisibleNameInfo(node, id);
		const hasVisibleNameSource = visibleInfo.value.length > 0;
		const isMismatch = hasVisibleNameSource && !this.areNamesEquivalent(node, visibleInfo.value, ariaLabel);
		const issue = isMismatch
			? this.__( 'aria-label conflicts with visible label/text. Prefer visible label source or align both values.', 'a11yfix' )
			: '';

		return {
			index: index + 1,
			url: pageUrl,
			selector: buildUniqueSelector(node),
			tag,
			type,
			id,
			name,
			role,
			ariaLabel,
			visibleNameSource: visibleInfo.source,
			visibleNameValue: visibleInfo.value,
			hasVisibleNameSource,
			isMismatch,
			issue
		};
	}

	resolveVisibleNameInfo(node, controlId) {
		const associatedLabelText = this.getAssociatedLabelText(node, controlId);
		if (associatedLabelText.length > 0) {
			return {
				source: 'label',
				value: associatedLabelText
			};
		}

		if (node.tagName.toLowerCase() === 'input') {
			const inputType = (node.getAttribute('type') || 'text').toLowerCase();
			if (['button', 'submit', 'reset'].includes(inputType)) {
				const inputValue = String(node.getAttribute('value') || '').trim();
				if (inputValue.length > 0) {
					return {
						source: 'input-value',
						value: inputValue
					};
				}
			}
		}

		const textContent = this.getVisibleTextContent(node);
		if (textContent.length > 0) {
			return {
				source: 'text',
				value: textContent
			};
		}

		return {
			source: '',
			value: ''
		};
	}

	getAssociatedLabelText(controlNode, controlId) {
		const tag = controlNode.tagName.toLowerCase();
		if (!['input', 'select', 'textarea'].includes(tag)) {
			return '';
		}

		if (controlId) {
			const linkedLabel = controlNode.ownerDocument.querySelector(`label[for="${this.escapeSelector(controlId)}"]`);
			if (linkedLabel) {
				return String(linkedLabel.textContent || '').replace(/\s+/g, ' ').trim();
			}
		}

		const wrappingLabel = controlNode.closest('label');
		if (!wrappingLabel) {
			return '';
		}

		return String(wrappingLabel.textContent || '').replace(/\s+/g, ' ').trim();
	}

	areNamesEquivalent(node, left, right) {
		const leftNorm = this.normalizeComparableName(left);
		const rightNorm = this.normalizeComparableName(right);

		if (!leftNorm || !rightNorm) {
			return false;
		}

		if (leftNorm === rightNorm) {
			return true;
		}

		if (leftNorm.includes(rightNorm) || rightNorm.includes(leftNorm)) {
			return true;
		}

		const leftTokens = this.tokenizeComparableName(leftNorm);
		const rightTokens = this.tokenizeComparableName(rightNorm);

		if (leftTokens.length === 0 || rightTokens.length === 0) {
			return false;
		}

		const shorterTokens = leftTokens.length <= rightTokens.length ? leftTokens : rightTokens;
		const longerTokens = shorterTokens === leftTokens ? rightTokens : leftTokens;
		const longerTokenSet = new Set(longerTokens);
		const overlapCount = shorterTokens.filter((token) => longerTokenSet.has(token)).length;
		const shorterCoverage = overlapCount / Math.min(leftTokens.length, rightTokens.length);
		const longerCoverage = overlapCount / Math.max(leftTokens.length, rightTokens.length);
		const comparisonMode = this.resolveComparisonMode(node, leftTokens, rightTokens);

		if (comparisonMode === 'strict') {
			if (shorterCoverage === 1 && longerCoverage >= 0.6) {
				return true;
			}

			return false;
		}

		if (comparisonMode === 'loose') {
			if (overlapCount >= 2 && shorterCoverage >= 0.5) {
				return true;
			}

			if (shorterCoverage >= 0.6) {
				return true;
			}

			return false;
		}

		if (overlapCount === shorterTokens.length && shorterTokens.length >= 2) {
			return true;
		}

		if (shorterTokens.length === 1 && overlapCount === 1 && shorterTokens[0].length >= 5) {
			return true;
		}

		const overlapRatio = overlapCount / Math.min(leftTokens.length, rightTokens.length);
		if (overlapRatio >= 0.75) {
			return true;
		}

		return false;
	}

	resolveComparisonMode(node, leftTokens, rightTokens) {
		const tag = String(node?.tagName || '').toLowerCase();
		const role = String(node?.getAttribute?.('role') || '').toLowerCase();
		const strictTags = new Set(['button', 'input', 'select', 'textarea', 'summary']);
		const strictRoles = new Set(['button', 'switch', 'checkbox', 'radio', 'tab', 'menuitem', 'textbox', 'combobox']);

		if (strictTags.has(tag) || strictRoles.has(role)) {
			return 'strict';
		}

		const isLinkLike = tag === 'a' || role === 'link';
		const longestTokenLength = Math.max(leftTokens.length, rightTokens.length);

		if (isLinkLike && longestTokenLength >= 5) {
			return 'loose';
		}

		return 'normal';
	}

	tokenizeComparableName(value) {
		if (!value) {
			return [];
		}

		return value
			.split(/\s+/)
			.map((token) => token.trim())
			.filter((token) => token.length >= 2);
	}

	getVisibleTextContent(node) {
		if (!node) {
			return '';
		}

		const clone = node.cloneNode(true);
		const hiddenSelector = '[aria-hidden="true"], [hidden], script, style, noscript';
		const hiddenNodes = clone.querySelectorAll(hiddenSelector);

		for (const hiddenNode of hiddenNodes) {
			hiddenNode.remove();
		}

		return String(clone.textContent || '').replace(/\s+/g, ' ').trim();
	}

	normalizeComparableName(value) {
		return String(value || '')
			.toLowerCase()
			.replace(/\s+/g, ' ')
			.replace(/[^a-z0-9 ]+/g, '')
			.trim();
	}

	escapeSelector(value) {
		if (window.CSS && typeof window.CSS.escape === 'function') {
			return window.CSS.escape(value);
		}

		return String(value).replace(/"/g, '\\"');
	}
}
